import { createContext, useMemo, FC, ReactNode, useState, useEffect, Dispatch, SetStateAction } from "react"

import { useAuth0, User } from "@auth0/auth0-react"
import useSWR from "swr"

import Analytics from "@/lib/Analytics"
import { captureException, setUser as setSentryUser } from "@/lib/Tracing"

import { userProfilesWithPreferencesByAuthProviderUserId } from "@/graphql/customQueries"

import { UserProfile, UserProfilesByAuthProviderUserIdQuery } from "@/API"

const CLAIM_NAMESPACE = "https://system-operating.ai"

export const ProfileContext = createContext<{
  user?: User
  userProfileId: string
  proxyId: string
  setUser: Dispatch<SetStateAction<User>>
  userProfile?: UserProfile
}>({
  user: undefined,
  setUser: () => {},
  userProfileId: "",
  proxyId: "",
  userProfile: undefined,
})

export const ProfileContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { user: _user } = useAuth0()
  const [user, setUser] = useState<User | undefined>(_user)
  const { data: userProfileData, error } = useSWR<UserProfilesByAuthProviderUserIdQuery>(
    [userProfilesWithPreferencesByAuthProviderUserId, { authProviderUserId: user?.sub }],
    { revalidateOnFocus: false }
  )
  const userProfile = userProfileData?.userProfilesByAuthProviderUserId?.items?.[0] as UserProfile

  useEffect(() => {
    setUser(_user)
    if (_user?.sub) {
      Analytics?.identify?.(_user?.sub)
      setSentryUser({ id: _user?.sub })
    } else {
      Analytics?.reset?.()
    }
  }, [_user])

  useEffect(() => {
    if (!userProfile) {
      return
    }
    localStorage.setItem("lang", userProfile.language || "en/gb")
  }, [userProfile])

  useEffect(() => {
    if (!error) {
      return
    }
    console.error(error)
    captureException(error)
  }, [error])

  const userProfileId = user?.[`${CLAIM_NAMESPACE}/userProfileId`]
  const proxyId = user?.[`${CLAIM_NAMESPACE}/proxyId`]

  const fields = useMemo(
    () => ({ user, setUser, userProfile, userProfileId, proxyId }),
    [proxyId, user, userProfile, userProfileId]
  )

  return <ProfileContext.Provider value={fields}>{children}</ProfileContext.Provider>
}
export default ProfileContext
